import { createStyles } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { useContext, useEffect, useMemo, type PropsWithChildren } from 'react';

import { UserContext } from '~/components/providers/UserProvider';
import { HEADER_HEIGHT } from '~/domains/common/components/AppShellHeader2/constants';
import { Routes } from '~/domains/common/constants/routes';
import { TabsDark } from '~/domains/design-system/components/TabsDark/TabsDark';
import Container from '~/domains/design-system/Container';

export const QUICK_PICKS_TABS_HEIGHT = 41;
export const QUICK_PICKS_TABS_HEIGHT_WITH_SUB_NAV = 135;

const useStyles = createStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100%',
  },
  tabs: {
    backgroundColor: theme.colors.gray2[6],
    borderTop: `1px solid ${theme.colors.gray[8]}`,
    height: QUICK_PICKS_TABS_HEIGHT,
    position: 'sticky',
    top: HEADER_HEIGHT,
    zIndex: 89,
  },
  content: {
    backgroundColor: theme.colors.gray2[0],
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  },
}));

export function QuickPickLayout({ children }: PropsWithChildren) {
  const { t } = useTranslation('contest');
  const { classes } = useStyles();
  const { isUserSignedIn } = useContext(UserContext);
  const router = useRouter();
  const currentPath = router.asPath;
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPath]);

  const tabs = useMemo(
    () => [
      {
        href: Routes.quickPicksBoard(),
        label: t('quickPick.tabs.board'),
      },
      {
        href: Routes.quickPicksActive(),
        label: t('quickPick.tabs.active'),
      },
      {
        href: Routes.quickPicksFinal(),
        label: t('quickPick.tabs.final'),
      },
    ],
    [t]
  );

  return (
    <div className={classes.container}>
      {isUserSignedIn && (
        <div className={classes.tabs}>
          <Container>
            <TabsDark.List>
              {tabs.map((tab) => (
                <TabsDark.Tab
                  key={tab.href}
                  href={tab.href}
                  isActive={currentPath.startsWith(tab.href)}
                >
                  {tab.label}
                </TabsDark.Tab>
              ))}
            </TabsDark.List>
          </Container>
        </div>
      )}
      <div className={classes.content}>{children}</div>
    </div>
  );
}
